import React, {useEffect} from 'react';
import clsx from 'clsx';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from "@mui/icons-material/Delete";
import { Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  userProjections,
  defaultProjection,
  defaultTransform,
  uniqueProjectionID
} from './projections';
import {useState} from 'react';
import {
  AppBar,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import LocalScene, { isValidProjection } from '../viewer/js/projections';
import {
  DropdownWithLabel,
  EnhancedTable,
  InputWithLabel,
  PromptDialog,
  TabPanel,
  TabProps
} from '../components';
import { toast } from '../app';
import { useTranslation } from 'react-i18next';
import { useDialog, useUniqueProjectID } from '../hooks';
import { registerEvent } from '../electron-modules';
import { useTableState } from '../hooks/use-table-state';
import { EnhancedTableState } from '../types/enhanced-table';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      margin: 0,
      padding: theme.spacing(1,2,1,2),
    },
    content: {
      padding: theme.spacing(0),
      paddingTop: "0px !important"
    },
    titleClose: {
      color: "white"
    },
    marginTop: {
      marginTop: theme.spacing(2)
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    adornedEnd: {
      paddingRight: theme.spacing(0)
    },
    utmParent: {
      display: "flex"
    },
    utmZone: {
      flex: 2
    },
    utmButton: {
      flex: 1,
      marginLeft: theme.spacing(1)
    },
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1)
    },
    dropdown: {
      minWidth: 200,
    },
    formControl: {
      width: '100%'
    },
    buttonDiv: {
      textAlign: "right"
    },
    tabsParent: {
      display: "flex",
      padding: theme.spacing(0, 1)
    },
    tabsHeaders: {
      flex: 1
    }
  }),
);

interface UserProjectionPanelProps {
  loadProjectionToEdit: (projectionData, newProjection) => void;
  setActiveTab: (value) => void;
  tableState: EnhancedTableState;
}

const UserProjectionPanel = (props: UserProjectionPanelProps) => {
  const {loadProjectionToEdit, setActiveTab, tableState} = props;
  const {t} = useTranslation();

  const deleteDialog = useDialog();

  const columns = [
    {
      id: 'name',
      label: t('projections.projection-name'),
      searchable: true
    },
    {
      id: 'type',
      label: t('projections.projection-type')
    },
    {
      id: 'custom',
      label: t('projections.custom-parameters')
    },
  ];

  // Convert projections to data row
  const data = userProjections.data;

  const getRows = () => {
    return data.map(option => {
      return {
        id: uniqueProjectionID(option.name),
        name: option.name,
        custom: option.transform ? t('general.yes') : t('general.no'),
        type: option.type,
        disabled: LocalScene.isDataProjection(option.name)
      };
    });
  };

  const onClick = (row) => {
    const results = data.filter(x => x.name === row.name);
    if (results.length !== 1) {
      return;
    }

    const projection = results[0];
    loadProjectionToEdit(projection, false);
    setActiveTab(1);
  };

  const onDelete = (row) => {
    const results = data.filter(x => x.name === row.name);
    if (results.length !== 1) {
      return;
    }

    // Delete projection + force reload
    const projection = results[0];
    userProjections.delete(projection.name);
    toast.error(t("toast.projection-deleted"));

    // Force projection list refresh
    setActiveTab(0);
  };

  const rows = getRows();

  const actions = [
    {
      title: t("buttons.delete"),
      icon: <DeleteIcon fontSize="small"/>,
      onClick: (row) => {
        deleteDialog.handleOpen(row);
      }
    }
  ];

  let deletePrompt = "";
  if (deleteDialog.open) {
    deletePrompt = t('projections.do_you_want_to_delete_this_projection_row_name', {
      name: deleteDialog.data.name
    });
  }

  return (
    <React.Fragment>
      <div>
        {rows.length === 0 && (<Typography variant="subtitle1">
          {t('projections.no-user-projections-have-been-added')}
        </Typography>)}

        {(rows.length > 0) && (<EnhancedTable
          state={tableState}
          columns={columns}
          rows={rows}
          actions={actions}
          onClick={onClick}
          maxHeight={"400px"}
        />)}
      </div>

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={() => {
          onDelete(deleteDialog.data);
          deleteDialog.handleClose();
        }}
        title={t("enhanced-table.prompt-delete-title")}
        prompt={deletePrompt}
        button={t('buttons.delete')}
      />

    </React.Fragment>
  );
};

interface EditPanelProps {
  setActiveTab: (value) => void;
  customProjection: any;
  hasTransform: boolean;
  newProjection: boolean;
  existingName: string;
  updateEditableValues: (values) => void
  setHasTransform: React.Dispatch<any>;
}

const EditPanel = (props: EditPanelProps) => {
  const {customProjection, updateEditableValues,
    hasTransform, setHasTransform,
    newProjection, setActiveTab, existingName} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const name = customProjection.name;
  const type = customProjection.type;
  const projString = customProjection.string;

  const transform = customProjection.transform;
  const origin = transform.origin;
  const translate = transform.translation;
  const rotation = transform.rotation;
  const scale = transform.scale;

  const handleChecked = (event) => {
    setHasTransform(event.target.checked);
  };

  const saveProjection = () => {
    // Create copy of our data
    let data = JSON.parse(JSON.stringify(customProjection));

    // Use trimmed versions of name and string
    data.name = nameTrim;
    data.string = stringTrim;

    if (!hasTransform) {
      delete data.transform;
    }

    const nameChanged = data.name !== existingName;
    const duplicateName = userProjections.getByName(data.name);

    // Check for duplicates if our name has changed
    if (nameChanged && duplicateName) {
      toast.error(t("toast.projection-duplicate"));
      return;
    }

    // Check for valid projection string
    let validProjection = isValidProjection(projString);
    if (!validProjection) {
      toast.error(t("toast.projection-invalid"));
      return;
    }

    const success = newProjection
      ? userProjections.save(data)
      : userProjections.update(existingName, data);

    if (!success) return;

    if (newProjection) {
      toast.success(t("toast.projection-added"));
    } else {
      toast.success(t("toast.projection-updated"));
    }

    setActiveTab(0);
  };

  // Remove excess whitespace from name and string
  let nameTrim = name.trim();
  let stringTrim = projString.trim();

  let originXError = origin[0] === "";
  let originYError = origin[1] === "";
  let originZError = origin[2] === "";

  let translateXError = translate[0] === "";
  let translateYError = translate[1] === "";
  let translateZError = translate[2] === "";

  let rotationError = rotation === "";
  let scaleError = scale === "";

  let canSubmit = (nameTrim !== "")
    && stringTrim !== "";

  if (hasTransform) {
    canSubmit = canSubmit
      && !originXError
      && !originYError
      && !originZError
      && !translateXError
      && !translateYError
      && !translateZError
      && !rotationError
      && !scaleError;
  }

  const updateType = (value) => {
    // Update projection type and clear projection string
    updateEditableValues({
      'type': value,
      'string': ""
    });
  };

  const updateName = (value) => {
    updateEditableValues({"name": value});
  };

  const updateString = (value) => {
    let string = (customProjection['type'] === "epsg")
      ? `EPSG:${value}`
      : value;

    updateEditableValues({"string": string});
  };

  const updateOrigin = (event, index) => {
    const value = parseFloat(event.target.value);
    let transform = customProjection.transform;
    transform['origin'][index] = isNaN(value) ? "" : value;

    updateEditableValues({
      "transform":transform
    });
  };

  const updateTranslation = (event, index) => {
    const value = parseFloat(event.target.value);
    let transform = customProjection.transform;
    transform['translation'][index] = isNaN(value) ? "" : value;

    updateEditableValues({
      "transform":transform
    });
  };

  const updateScale = (event) => {
    const value = parseFloat(event.target.value);
    let transform = customProjection.transform;
    transform['scale'] = isNaN(value) ? "" : value;

    updateEditableValues({
      "transform":transform
    });
  };

  const updateRotation = (event) => {
    let value = parseFloat(event.target.value);
    let transform = customProjection.transform;
    transform['rotation'] = isNaN(value) ? "" : value;

    updateEditableValues({
      "transform":transform
    });
  };

  return (
    <div>

      <Grid container spacing={2}>

        {/** Projection Type */}
        <Grid item xs={12}>
          <DropdownWithLabel
            title={t('projections.projection-type')}
            value={type}
            onChange={updateType}
            options={[
              ["proj4", t('projections.projection-string-proj4')],
              ["wkt", t('projections.well-known-text-wkt')],
            ]}
          />
        </Grid>

        {/** Projection Name */}
        <Grid item xs={12}>
          <InputWithLabel
            title={t('projections.projection-name')}
            placeholder={t('projections.custom-projection')}
            value={name}
            onChange={updateName}
          />
        </Grid>

        {/** Projection string (proj4) */}
        {type==="proj4" && (<Grid item xs={12}>
          <InputWithLabel
            multiline
            title={t('projections.proj4-definition')}
            placeholder="+proj=utm +zone=10 +ellps=WGS84 +datum=WGS84 +units=m"
            value={projString}
            onChange={updateString}
          />
        </Grid>)}

        {/** Projection string (WKT) */}
        {type==="wkt" && (<Grid item xs={12}>
          <InputWithLabel
            multiline
            title={t('projections.wkt-definition')}
            placeholder='PROJCS["NAD83(CSRS) / UTM zone 10N",GEOGCS["NAD83(CSRS)",DATUM["D_North_American_1983_CSRS98",SPHEROID["GRS_1980",6378137,298.257222101]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",-123],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],UNIT["Meter",1]]'
            value={projString}
            onChange={updateString}
          />
        </Grid>)}

      </Grid>

      <FormControlLabel
        className={classes.marginTop}
        control={
          <Checkbox
            checked={hasTransform}
            onChange={handleChecked}
          />
        }
        label={t('projections.additional-parameters')}
      />

      {hasTransform && (<Grid container >
        {/* Origin */}
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <TextField
              label={t('projections.origin-x')}
              type="number"
              value={origin[0]}
              onChange={(e) => {
                updateOrigin(e, 0);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={originXError}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label={t('projections.origin-y')}
              type="number"
              value={origin[1]}
              onChange={(e) => {
                updateOrigin(e, 1);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={originYError}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label={t('projections.origin-z')}
              type="number"
              value={origin[2]}
              onChange={(e) => {
                updateOrigin(e, 2);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={originZError}
            />
          </Grid>
        </Grid>

        {/* Translation */}
        <Grid className={classes.marginTop} container spacing={3}>
          <Grid item xs={4}>
            <TextField
              label={t('projections.translation-x')}
              type="number"
              value={translate[0]}
              onChange={(e) => {
                updateTranslation(e, 0);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={translateXError}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label={t('projections.translation-y')}
              type="number"
              value={translate[1]}
              onChange={(e) => {
                updateTranslation(e, 1);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={translateYError}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label={t('projections.translation-z')}
              type="number"
              value={translate[2]}
              onChange={(e) => {
                updateTranslation(e, 2);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={translateZError}
            />
          </Grid>
        </Grid>

        {/* Rotation and scaling */}
        <Grid className={classes.marginTop} container spacing={3}>
          <Grid item xs={4}>
            <TextField
              label={t('projections.horizontal-scale')}
              fullWidth
              type="number"
              value={scale}
              onChange={updateScale}
              InputLabelProps={{
                shrink: true,
              }}
              error={scaleError}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label={t('projections.rotation')}
              fullWidth
              type="number"
              value={rotation}
              onChange={updateRotation}
              InputLabelProps={{
                shrink: true,
              }}
              error={rotationError}
            />
          </Grid>
        </Grid>
      </Grid>)}

      <div className={classes.buttonDiv}>
        {!newProjection && (<Button
          color="primary"
          type="submit"
          onClick={() => {
            setActiveTab(0);
          }}
          className={classes.button}
        >
          {t("buttons.cancel")}
        </Button>)}

        <Button
          color="primary"
          type="submit"
          onClick={saveProjection}
          className={classes.button}
          disabled={!canSubmit}
        >
          {newProjection ? t('projections.add-projection') : t('projections.save-changes')}
        </Button>
      </div>
    </div>
  );
};

interface SearchPanelProps {
  loadProjectionToEdit: (projectionData, newProjection) => void;
  setActiveTab: (value) => void;
  tableState: EnhancedTableState;
}

const SearchPanel = (props: SearchPanelProps) => {
  const {tableState, setActiveTab, loadProjectionToEdit} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [type, setType] = useState("text");
  const [searchTerm, setSearchTerm] = useState("");
  const [hemisphere, setHemisphere] = useState("N");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const handleChange = (event) => {
    setType(event.target.value);
  };

  const handleHemisphere = (event) => {
    setHemisphere(event.target.value);
  };

  const handleString = (event) => {
    setSearchTerm(event.target.value);
  };

  const searchOnline = () => {
    if (searchTerm === "") {
      return;
    }

    let value = searchTerm;
    if (type === "utm") {
      value = `utm ${value}${hemisphere}`;
    }

    tableState.reset();
    getProjections(value);
  };

  const getProjections = (searchText, pageNumber = 1, previousResults = [], maxPageNumber = 10) => {
    if (pageNumber === 1) {
      setSearchResults([]);
      setSearching(true);
    }

    const text = type !== "epsg" ?
      `${searchText} kind:PROJCRS` :
      searchText;

    const url = "https://epsg.io/?q=" + text + "&format=json&page=" + pageNumber;

    fetch(url)
      .then(response => response.json())
      .then(data => {
        const results = data.results;
        const parsed = [];

        results.forEach(result => {
          if (type === "epsg") {
            if (result.code !== searchText) {
              return;
            }
          }

          if (result.proj4 === "") {
            return;
          }

          parsed.push(result);
        });

        const combined = [...previousResults, ...parsed];
        setSearchResults(combined);

        if ((results.length > 0) && (pageNumber < maxPageNumber)) {
          setTimeout(() =>{
            getProjections(searchText, pageNumber + 1, combined);
          }, 250);
        } else {
          setSearching(false);
        }
      });
  };

  const onClick = (row) => {
    loadProjectionToEdit({
      type: "proj4",
      name: row.name,
      string: row.proj4
    }, true);
    setActiveTab(1);
  };

  const getRows = () => {
    return searchResults.map(projection => {
      return {
        id: uniqueProjectionID(projection.name),
        name: projection.name as string,
        epsg: parseInt(projection.code),
        proj4: projection.proj4 as string,
      };
    });
  };

  const columns = [
    {
      id: 'name',
      label: t('projections.name')
    },
    {
      id: 'epsg',
      label: t('projections.epsg'),
      numeric: true
    },
    {
      id: 'proj4',
      label: t('projections.proj4-string')
    },
  ];

  const rows = getRows();

  return (
    <div>
      {/* Search Type */}
      <div className={classes.marginBottom}>
        <InputLabel id="proj-type-select-label">
          {t('projections.search-type')}
        </InputLabel>
        <Select
          fullWidth
          labelId="proj-type-select-label"
          id="search-type-select"
          value={type}
          onChange={handleChange}
          className={classes.dropdown}
        >
          <MenuItem value={"text"}>{t('projections.text')}</MenuItem>
          <MenuItem value={"utm"}>{t('projections.utm-zone')}</MenuItem>
          <MenuItem value={"epsg"}>{t('projections.epsg-code')}</MenuItem>
        </Select>
      </div>

      {/* UTM Zone */}
      {type==="utm" && (<div className={classes.utmParent}>
        <TextField
          label={t('projections.utm-zone')}
          placeholder='10'
          type="number"
          margin="dense"
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handleString}
          InputProps={{
            startAdornment: <InputAdornment position="start">
              {t('projections.utm-zone')}
            </InputAdornment>
          }}
          className={clsx(classes.marginTop, classes.utmZone)}
        />

        <TextField
          select
          label={t('projections.hemisphere')}
          margin="dense"
          value={hemisphere}
          onChange={handleHemisphere}
          className={clsx(classes.marginTop, classes.utmButton)}
        >
          <MenuItem key={"N"} value="N">{t('directions.north')}</MenuItem>
          <MenuItem key={"S"} value="S">{t('directions.south')}</MenuItem>
        </TextField>

      </div>)}

      {/* EPSG Code */}
      {type==="epsg" && (<TextField
        fullWidth
        label={t('projections.epsg-number')}
        placeholder='3857'
        type="number"
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        onChange={handleString}
        InputProps={{
          startAdornment: <InputAdornment position="start">EPSG:</InputAdornment>,
        }}
        className={classes.marginTop}
      />)}

      {/* Text Search */}
      {type==="text" && (<TextField
        label={t('projections.text-search')}
        placeholder="texas north"
        fullWidth
        multiline
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        onChange={handleString}
        className={classes.marginTop}
      />)}

      {/* Search button */}
      <div className={classes.buttonDiv}>
        <Button
          color="primary"
          type="submit"
          disabled={searching}
          onClick={searchOnline}
          className={classes.button}
        >
          {searching ? t('projections.searching-epsg-io') : t('projections.search-online')}
        </Button>
      </div>

      {/* Results table */}
      {(rows.length > 0) && <EnhancedTable
        state={tableState}
        columns={columns}
        rows={rows}
        onClick={onClick}
        maxHeight={"300px"}
      />}
    </div>
  );
};

interface StyledPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}

const StyledPanel = (props: StyledPanelProps) => {
  return (
    <TabPanel
      PanelProps={{
        style: {
          minHeight: "240px"
        }
      }}
      value={props.value}
      index={props.index}
    >
      {props.children}
    </TabPanel>
  );
};

export const ProjectionsManager = () => {
  const classes = useStyles();
  const {t} = useTranslation();
  const uniqueProjectID = useUniqueProjectID();
  const projectionsDialog = useDialog();

  // Must be defined here to persist (dialog is not mounted)
  const userTableState = useTableState({defaultSort: "name"});
  const searchTableState = useTableState();

  const [existingName, setExistingName] = useState("");
  const [newProjection, setNewProjection] = useState(false);
  const [activeTab, setTabValue] = useState(0);
  const [customProjection, setCustomProjection] = useState({});
  const [hasTransform, setHasTransform] = useState(false);

  const setActiveTab = (value) => {
    if (value === 0) {
      clearProjectionValues();
    }

    setTabValue(value);
  };

  const updateEditableValues = (newValues) => {
    const combined = {...customProjection, ...newValues};
    setCustomProjection(combined);
  };

  const clearProjectionValues = () => {
    loadProjectionToEdit(defaultProjection, true);
  };

  const loadProjectionToEdit = (projectionData, newProjection) => {
    const dataCopy = JSON.parse(JSON.stringify(projectionData));
    const hasTransform = dataCopy.hasOwnProperty("transform");
    if (!hasTransform) {
      const transformCopy = JSON.parse(JSON.stringify(defaultTransform));
      dataCopy.transform = transformCopy;
    }

    setHasTransform(hasTransform);
    setCustomProjection({...dataCopy});
    setExistingName(dataCopy.name);
    setNewProjection(newProjection);
  };

  const handleChange = (event, newactiveTab) => {
    setActiveTab(newactiveTab);
  };

  useEffect(() => {
    /** Close dialog when new project is loaded */
    projectionsDialog.handleClose();
  }, [uniqueProjectID]);

  useEffect(() => {
    registerEvent("open-projection-manager", () => {
      projectionsDialog.handleOpen();
    });
  }, []);

  useEffect(() => {
    clearProjectionValues();
  }, []);

  useEffect(() => {
    if (!projectionsDialog.open) return;
    userProjections.load();
  }, [projectionsDialog.open]);

  return (
    <div>
      <Dialog
        fullWidth
        scroll="body"
        open={projectionsDialog.open}
        onClose={projectionsDialog.handleClose}
      >

        <AppBar position="static">
          <div className={classes.tabsParent}>
            <Tabs
              value={activeTab}
              onChange={handleChange}
              className={classes.tabsHeaders}
            >
              <Tab label={t('projections.user-projections')} {...TabProps(0)} />
              {newProjection && (<Tab label={t('projections.add-new')} {...TabProps(1)} />)}
              {!newProjection && (<Tab label={t('projections.edit-existing')} {...TabProps(1)} />)}
              <Tab label={t('projections.search-online')} {...TabProps(2)} />
            </Tabs>

            {/* Close button */}
            <IconButton
              aria-label="close"
              className={classes.titleClose}
              onClick={projectionsDialog.handleClose}
              size="large">
              <CloseIcon fontSize="small"/>
            </IconButton>

          </div>
        </AppBar>

        {/* Existing projections */}
        <StyledPanel value={activeTab} index={0}>
          <UserProjectionPanel
            loadProjectionToEdit={loadProjectionToEdit}
            setActiveTab={setActiveTab}
            tableState={userTableState}
          />
        </StyledPanel>

        {/* Custom projections */}
        <StyledPanel value={activeTab} index={1}>
          <EditPanel
            customProjection={customProjection}
            updateEditableValues={updateEditableValues}
            hasTransform={hasTransform}
            setHasTransform={setHasTransform}
            newProjection={newProjection}
            existingName={existingName}
            setActiveTab={setActiveTab}
          />
        </StyledPanel>

        {/* Online projection search */}
        <StyledPanel value={activeTab} index={2}>
          <SearchPanel
            setActiveTab={setActiveTab}
            loadProjectionToEdit={loadProjectionToEdit}
            tableState={searchTableState}
          />
        </StyledPanel>
      </Dialog>
    </div>
  );
};
